// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '../../../../store/rootOrganization';
import useContractStore from '@/app/store/modules/contract';
import useMerchantAccountStore from '@/app/store/modules/merchantAccount';
import actionsContracts from '../../api/actionsContracts';
// Components
import DataTable from '../../../../../common-utils/components/renders/dataTable/DataTable';
// Common
import { columnsContract, columnsContractId } from './columnDef/columnsContract';
import getInitialQuery from '@/common-utils/components/renders/dataTable/utils/getInitialQuery';
import mergeQuery from '@/common-utils/components/renders/dataTable/utils/mergeQuery';
import Permissions from '@/app/permissions/Permissions';
import CONTRACT_ACCESS from '@/app/modules/accounts/api/apiUrls';
import ContractsImportProps from '../import/ContractsImportProps';

/**
 *
 * @param root0
 * @param root0.merchantAccountId
 * @param root0.customQuery
 * @param root0.singleSelection
 * @param root0.rowsSelected
 * @param root0.setRowsSelected
 * @param root0.pageActions
 * @param root0.setIsLoading
 * @param root0.filtersEventName
 * @returns {JSX} ContractsTable
 */
function ContractsTable({
    merchantAccountId,
    customQuery,
    singleSelection = false,
    rowsSelected,
    setRowsSelected,
    pageActions = undefined,
    setIsLoading,
    filtersEventName
}) {
    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const { refetchContracts: refetchFromStore, storeRefetchContracts, query: queryStored, storeQuery } = useContractStore();
    const { merchantAccountDetails, storeMerchantAccountDetails } = useMerchantAccountStore();

    // Constants
    const initialQuery = getInitialQuery(customQuery);
    const selectionMode = !!(rowsSelected && setRowsSelected);

    // State
    const [query, setQuery] = useState(mergeQuery(initialQuery, queryStored));
    const [launchRefetch, setLaunchRefetch] = useState(true);

    // Api Calls
    const { data, isFetching, refetch } = actionsContracts.getContracts({ query: query, merchantAccountId: merchantAccountId }, false);

    // Hooks
    useEffect(() => {
        if (rootOrganization && (launchRefetch || refetchFromStore)) {
            refetch();
            setLaunchRefetch(false);
            storeRefetchContracts(false);
        }
    }, [query, launchRefetch, refetchFromStore]);

    // Extra hook to store mechant account details
    useEffect(() => {
        if (merchantAccountDetails?.uuid !== data?.merchantDetails?.uuid && data?.merchantDetails) {
            storeMerchantAccountDetails(data.merchantDetails);
        }
    }, [data]);

    useEffect(() => {
        if (setIsLoading) {
            setIsLoading(isFetching);
        }
    }, [isFetching]);

    const allColumns = columnsContract({ query, setQuery, queryStored, storeQuery, setLaunchRefetch, selectionMode, singleSelection, filtersEventName });

    // Render
    return (
        <DataTable
            filtersEventName={filtersEventName}
            columns={allColumns}
            data={data}
            dataFetchingIsLoading={isFetching}
            columnsId={columnsContractId}
            initialQuery={initialQuery}
            query={query}
            setQuery={setQuery}
            queryStored={queryStored}
            storeQuery={storeQuery}
            setLaunchRefetch={setLaunchRefetch}

            selectionMode={selectionMode}
            singleSelection={singleSelection}
            rowsSelected={rowsSelected}
            setRowsSelected={setRowsSelected}

            pageActions={pageActions}
            exportProps={{
                permissions: [Permissions.EXPORT_CONTRACTS.name],
                api: CONTRACT_ACCESS.EXPORT_CONTRACTS,
                uuid: merchantAccountId
            }}
            importProps={{
                ...ContractsImportProps,
                pathParams: { contractAccessUuid: merchantAccountId },
                setLaunchRefetch,
                permissions: [Permissions.CSV_ADD_CONTRACT_PROFILE.name]
            }}
        />
    );
}

ContractsTable.propTypes = {
    merchantAccountId: PropTypes.string.isRequired,
    singleSelection: PropTypes.bool,
    rowsSelected: PropTypes.object,
    customQuery: PropTypes.object,
    setRowsSelected: PropTypes.func,
    pageActions: PropTypes.node,
    setIsLoading: PropTypes.func,
    filtersEventName: PropTypes.string
};

export default ContractsTable;